import { authGuard } from '@auth0/auth0-vue';
import { i18n } from '@/i18n';
import { useAccountsStore } from '@/accounts/store';
import { useGlobalStore } from '@/stores/global';
import { authCallbackError } from '@/authentication/auth0';

const getAuthErrorRoute = (authError) => {
    const { error, error_description: errorDescription } = authError;
    const query = new URLSearchParams();

    if (error) query.set('error', error);
    if (errorDescription) query.set('error_description', errorDescription);

    const queryString = query.toString();

    return `/auth-error${queryString ? `?${queryString}` : ''}`;
};

// Global beforeEach guard. Public routes skip auth entirely. When Auth0 returns a
// terminal login/callback error (captured at boot from ?error=&state=), skip
// authGuard — otherwise loginWithRedirect would re-hit Auth0, return the same
// error, and loop. Redirecting to /auth-error (a public route) breaks the loop.
// Do not key this off the shared SDK error ref; silent token failures also set it.
export const requireAuth = async (to) => {
    if (to.meta?.isPublic) return true;

    if (authCallbackError) {
        return getAuthErrorRoute(authCallbackError);
    }

    return authGuard(to);
};

const setTitle = (route) => {
    const baseTitle = i18n.global.t(route.meta.title || 'global.title', { productName: i18n.global.t('global.productName') });

    setTimeout(() => {
        document.title = baseTitle;
    });
};

const routeHasRequiredFeatureFlag = (route) => {
    const globalStore = useGlobalStore();
    const { featureFlags } = globalStore;
    const { featureFlag } = route.meta;

    return Boolean(!featureFlag || (featureFlags && featureFlags[featureFlag]));
};

const routeIsValid = (to) => {
    return to.matched.every((route) => {
        return routeHasRequiredFeatureFlag(route);
    });
};

export const beforeResolveRoute = async (to, _, next) => {
    const accountsStore = useAccountsStore();
    const appId = to.query.app_id || to.params.appId;

    if (appId) {
        accountsStore.updateAppId(appId);
    } else if (window.__KEAP__.VUE_APP_APP_ID) {
        accountsStore.updateAppId(window.__KEAP__.VUE_APP_APP_ID);
    }

    if (!to.meta.isPublic) {
        const globalStore = useGlobalStore();

        if (!globalStore.boot.isAuthorized) {
            await globalStore.loadBoot();
        }
    }

    if (!routeIsValid(to)) {
        return next('/');
    }

    setTitle(to);

    return next();
};
